// UserAppPairAllowlistFromAlertModal — quick-suppress modal opened from the
// Detection Alerts Actions column (v1.83.0). Pre-fills the (user, application) pair
// straight from the alert row (the alert already carries application_id +
// app_display_name + user_id), so unlike the mailbox-pair modal there's no source-
// events blob to parse. The analyst can optionally widen user→* and/or app→*
// (the launch pattern is user=* for a benign first-party app), add a note, confirm.
// POSTs via addUserAppPairAllowlistEntry, which gates on the rule's
// skip_if_in_user_app_pair_allowlist toggle and bumps the version sentinel.

import React, { useState, useMemo } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { addUserAppPairAllowlistEntry } from '../../api/client';

const UPN_REGEX = /^[^@\s|]+@[^@\s|]+\.[^@\s|]+$/;
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function UserAppPairAllowlistFromAlertModal({ alert, onClose }) {
  const alertUpn = (alert.userId || '').trim();
  const alertAppId = (alert.applicationId || '').trim();
  const alertAppName = (alert.appDisplayName || '').trim();

  const userIsValidUpn = UPN_REGEX.test(alertUpn);
  const appIsValidGuid = GUID_REGEX.test(alertAppId);
  // Domain of the alert's UPN (for the "any guest from a domain" option).
  const alertDomain = userIsValidUpn ? alertUpn.split('@')[1].toLowerCase() : '';

  // userMode: 'this' (exact UPN) | 'domain' (*@domain — B2B guests only) | 'any' (*)
  // Default narrowest valid suppression: the specific user when usable, else all-users.
  const [userMode, setUserMode] = useState(userIsValidUpn ? 'this' : 'any');
  const [anyApp, setAnyApp] = useState(!appIsValidGuid);
  const [note, setNote] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  const effectiveUser = userMode === 'any' ? '*' : userMode === 'domain' ? `*@${alertDomain}` : alertUpn.toLowerCase();
  const effectiveAppId = anyApp ? '*' : alertAppId.toLowerCase();

  const bothWildcard = effectiveUser === '*' && effectiveAppId === '*';

  const blocker = useMemo(() => {
    if (!alert.ruleId) return 'This alert has no rule id.';
    if (!alert.tenantId) return 'This alert has no tenant id.';
    if (userMode === 'this' && !userIsValidUpn) return 'The alert user is not a full UPN — choose another user option.';
    if (userMode === 'domain' && !alertDomain) return 'The alert user has no domain — choose another user option.';
    if (!anyApp && !appIsValidGuid) return 'The alert has no application GUID — choose "Any app (*)".';
    if (bothWildcard) return 'The (any user, any app) pair is not allowed — narrow at least one side.';
    return null;
  }, [alert.ruleId, alert.tenantId, userMode, anyApp, userIsValidUpn, alertDomain, appIsValidGuid, bothWildcard]);

  const handleApply = async () => {
    if (blocker) { setError(blocker); return; }
    setApplying(true);
    setError(null);
    try {
      const res = await addUserAppPairAllowlistEntry({
        ruleId:                 alert.ruleId,
        tenantId:               alert.tenantId,
        userPrincipalName:      effectiveUser,
        applicationId:          effectiveAppId,
        applicationDisplayName: anyApp ? '' : alertAppName,
        note:                   note.trim(),
        enabled:                true,
      });
      setApplied({ pendingSync: !!res?.pendingSync, created: !!res?.created });
    } catch (err) {
      setError(err.message || 'Failed to add allowlist entry');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Suppress (user, application) pair</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {alert.ruleName ? <span className="text-gray-400">{alert.ruleName}</span> : null}
              {' '}· Tenant <span className="font-mono">{alert.tenantId}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {applied ? (
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-lg text-sm text-emerald-200 flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{applied.created ? 'Pair allowlisted.' : 'Pair updated (already existed).'}</p>
                <p className="text-emerald-300/80 mt-1">
                  <span className="font-mono">{effectiveUser}</span> + <span className="font-mono">{effectiveAppId}</span> will no longer alert on this rule.
                  {applied.pendingSync && ' The sentinel bump is pending retry (~30 s); the pipeline will pick it up shortly.'}
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                The pipeline matches on the <strong>application GUID</strong>; the display name is informational. A
                known-malicious IP still alerts. Re-adding an existing pair re-enables it.
              </p>

              {/* User */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">User</label>
                <div className="space-y-1.5 text-sm">
                  <label className={`flex items-center gap-2 ${userIsValidUpn ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                    <input type="radio" name="userSel" checked={userMode === 'this'} disabled={!userIsValidUpn}
                      onChange={() => setUserMode('this')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                    <span className="text-gray-300">This user: <span className="font-mono text-gray-200">{alertUpn || '—'}</span>{!userIsValidUpn && ' (not a full UPN)'}</span>
                  </label>
                  <label className={`flex items-start gap-2 ${alertDomain ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                    <input type="radio" name="userSel" checked={userMode === 'domain'} disabled={!alertDomain}
                      onChange={() => setUserMode('domain')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      Any guest from <span className="font-mono text-gray-200">{alertDomain || '—'}</span>
                      <span className="text-[11px] text-gray-500 block">Only external (B2B) guests whose UPN ends with this domain — internal members are never affected.</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="userSel" checked={userMode === 'any'} onChange={() => setUserMode('any')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                    <span className="text-amber-300">Any user (*)</span>
                  </label>
                </div>
              </div>

              {/* Application */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Application</label>
                <div className="space-y-1.5 text-sm">
                  <label className={`flex items-start gap-2 ${appIsValidGuid ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                    <input type="radio" name="appSel" checked={!anyApp} disabled={!appIsValidGuid}
                      onChange={() => setAnyApp(false)}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      This app: <span className="text-gray-200">{alertAppName || <span className="italic text-gray-500">(no name)</span>}</span>
                      <span className="font-mono text-[11px] text-gray-500 block truncate">{alertAppId || '(no application GUID on this alert)'}</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="appSel" checked={anyApp} onChange={() => setAnyApp(true)}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                    <span className="text-amber-300">Any app (*)</span>
                  </label>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Note</label>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Why is this pair benign?" className="input-field w-full" />
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {!error && blocker && (
                <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded text-amber-300 text-xs flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{blocker}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          {applied ? (
            <button onClick={onClose} className="btn-primary px-4 py-1.5 text-sm">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={applying} className="btn-secondary px-4 py-1.5 text-sm">Cancel</button>
              <button onClick={handleApply} disabled={applying || !!blocker} className="btn-primary px-4 py-1.5 text-sm flex items-center gap-2">
                {applying && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Allowlist pair
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
